'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Calendar, Clock, Users, ChevronLeft, Save, Waves } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { divingPools } from '@/data/divingPools'
import { toast } from '@/components/ui/ToastContainer'
import logger from '@/utils/logger'
import { ButtonSkeleton, TextSkeleton } from '@/components/ui/Skeleton'
import { useTheme } from '@/contexts/ThemeContext'
import MatchingSuccessModal from '@/components/Matching/MatchingSuccessModal'
import { parseAuthToken } from '@/utils/base64'

export default function NewMatchingPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const supabase = getSupabaseClient()
  const [userProfile, setUserProfile] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [matchInfo, setMatchInfo] = useState<any>(null)
  const [additionalDivers, setAdditionalDivers] = useState<any[]>([])
  const [showMatchingModal, setShowMatchingModal] = useState(false)
  const [isWaitingForMatch, setIsWaitingForMatch] = useState(false)
  
  const [formData, setFormData] = useState({
    date: '',
    time_slot: 'flexible' as 'morning' | 'afternoon' | 'evening' | 'flexible',
    level_flexible: false,
    diving_pool: '',
    experience_level: '',
    description: '',
    preferred_group_size: 4
  })

  const [editMode, setEditMode] = useState(false)
  const [editRequestId, setEditRequestId] = useState<string | null>(null)

  const isTimeSlotAvailable = (slot: 'morning' | 'afternoon' | 'evening' | 'flexible', selectedDate: string): boolean => {
    if (!selectedDate) return true

    const now = new Date()
    const today = now.toISOString().split('T')[0]

    if (selectedDate !== today) return true

    const currentHour = now.getHours()

    switch (slot) {
      case 'morning': return currentHour < 12
      case 'afternoon': return currentHour < 18
      case 'evening': return currentHour < 20
      case 'flexible': return currentHour < 20
      default: return true
    }
  }

  useEffect(() => {
    if (formData.date && !isTimeSlotAvailable(formData.time_slot, formData.date)) {
      if (isTimeSlotAvailable('afternoon', formData.date)) {
        setFormData(prev => ({ ...prev, time_slot: 'afternoon' }))
      } else if (isTimeSlotAvailable('evening', formData.date)) {
        setFormData(prev => ({ ...prev, time_slot: 'evening' }))
      } else if (isTimeSlotAvailable('flexible', formData.date)) {
        setFormData(prev => ({ ...prev, time_slot: 'flexible' }))
      }
    }
  }, [formData.date])

  useEffect(() => {
    checkAuth()

    const editData = localStorage.getItem('edit_request_data')
    if (editData) {
      try {
        const data = JSON.parse(editData)
        setEditMode(true)
        setEditRequestId(data.id)

        const poolId = divingPools.find(p => p.name === data.diving_center)?.id || ''

        setFormData({
          date: data.date || '',
          time_slot: data.time_slot || 'flexible',
          level_flexible: data.level_flexible || false,
          diving_pool: poolId,
          experience_level: data.experience_level || '',
          description: data.description || '',
          preferred_group_size: data.preferred_group_size || 4
        })

        logger.debug('📝 [EDIT] Loaded edit data:', {
          ...data,
          level_flexible: data.level_flexible,
          diving_center: data.diving_center,
          found_pool_id: poolId
        })

        localStorage.removeItem('edit_request_data')
      } catch (error) {
        logger.error('Failed to load edit data:', error)
      }
    }
  }, [])

  useEffect(() => {
    if (!userProfile?.id) return

    logger.debug('[Matching] Subscribing to notifications for userId:', userProfile.id)

    const channel = supabase
      .channel(`matching-notifications-${userProfile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userProfile.id}`,
        },
        (payload) => {
          logger.debug('[Matching] New notification received:', payload.new)
          const notification = payload.new as any

          if (notification.type === 'match_found' && notification.data) {
            logger.debug('🎯 Match found via Realtime:', notification.data)
            setMatchInfo(notification.data)
            setShowMatchingModal(true)
            setIsWaitingForMatch(false)
          }
        }
      )
      .subscribe((status) => {
        logger.debug('[Matching] Subscription status:', status)
      })

    return () => {
      logger.debug('[Matching] Unsubscribing from notifications')
      supabase.removeChannel(channel)
    }
  }, [userProfile?.id])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token')

      if (!token) {
        router.push('/login')
        return
      }

      const decoded = parseAuthToken(token)
      if (!decoded) {
        logger.error('Token parsing failed')
        localStorage.removeItem('auth_token')
        router.push('/login')
        return
      }

      const supabase = getSupabaseClient()

      let query = supabase.from('user_profiles').select('*')

      if (decoded.kakao_id) {
        query = query.eq('kakao_id', decoded.kakao_id)
      } else if (decoded.naver_id) {
        query = query.eq('naver_id', decoded.naver_id)
      } else {
        logger.error('No valid provider ID in token')
        localStorage.removeItem('auth_token')
        router.push('/login')
        return
      }

      const { data, error } = await query.single()

      if (error || !data) {
        logger.error('Profile not found:', error)
        localStorage.removeItem('auth_token')
        window.location.href = '/'
        return
      }

      if (!data.profile_completed) {
        router.push('/profile/setup')
        return
      }

      setUserProfile(data)

      setFormData(prev => ({
        ...prev,
        experience_level: data.certification_level || ''
      }))
    } catch (error) {
      logger.error('Auth error:', error)
      router.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!userProfile) return

    if (!editMode && formData.date) {
      try {
        const { data: sameDateMatches, error } = await supabase
          .from('match_requests')
          .select('id, status, date, time_slot, time_flexible, diving_center')
          .eq('user_id', userProfile.id)
          .eq('date', formData.date)
          .in('status', ['pending', 'processing', 'matched'])

        if (error) {
          logger.error('중복 체크 오류:', error)
        } else if (sameDateMatches && sameDateMatches.length > 0) {
          const hasTimeConflict = sameDateMatches.some((match: any) => {
            if (match.time_slot === 'flexible' && formData.time_slot === 'flexible') return true
            if (match.time_slot === 'flexible' || formData.time_slot === 'flexible') return true
            if (match.time_slot === formData.time_slot) return true
            return false
          })

          if (hasTimeConflict) {
            toast.error(
              '같은 날짜/시간대 매칭 중복',
              `${formData.date}에 이미 매칭 요청이 있습니다. 같은 날짜/시간대에는 하나의 매칭만 신청할 수 있습니다.`
            )
            return
          }
        }
      } catch (error) {
        logger.error('중복 체크 실패:', error)
      }
    }

    setIsSaving(true)

    try {
      const selectedPool = divingPools.find(p => p.id === formData.diving_pool)
      const poolName = selectedPool?.name || formData.diving_pool
      const poolRegion = selectedPool?.region || '서울'

      logger.debug('매칭 요청 데이터 전송:', {
        date: formData.date,
        timeSlot: formData.time_slot,
        levelFlexible: formData.level_flexible,
        experienceLevel: formData.level_flexible ? null : formData.experience_level,
        region: poolRegion,
        divingCenter: poolName,
        urgencyLevel: 5
      })

      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('로그인이 필요합니다')
      }

      const apiUrl = editMode ? '/api/matching/update' : '/api/matching/request'
      const method = editMode ? 'PUT' : 'POST'

      logger.debug('🔧 [SUBMIT] Edit mode:', editMode)
      logger.debug('🆔 [SUBMIT] Edit request ID:', editRequestId)

      const requestBody = editMode ? {
        request_id: editRequestId,
        date: formData.date,
        timeSlot: formData.time_slot,
        levelFlexible: formData.level_flexible,
        experienceLevel: formData.level_flexible ? null : formData.experience_level,
        region: poolRegion,
        divingCenter: poolName,
        urgencyLevel: 5,
        preferredGroupSize: formData.preferred_group_size
      } : {
        date: formData.date,
        timeSlot: formData.time_slot,
        levelFlexible: formData.level_flexible,
        experienceLevel: formData.level_flexible ? null : formData.experience_level,
        region: poolRegion,
        divingCenter: poolName,
        urgencyLevel: 5,
        preferredGroupSize: formData.preferred_group_size
      }

      logger.debug('📤 [SUBMIT] Request body:', requestBody)

      const response = await fetch(apiUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json()

        const errorMsg = errorData.details
          ? `${errorData.error}: ${errorData.details} (${errorData.code})`
          : errorData.error || '매칭 요청 생성에 실패했습니다'
        throw new Error(errorMsg)
      }

      const result = await response.json()

      if (editMode) {
        const successMessage = result.reactivated
          ? '매칭이 재활성화되었습니다!'
          : '매칭 요청이 수정되었습니다.'
        toast.success('수정 완료', successMessage)
        localStorage.removeItem('edit_request_data')
        setTimeout(() => router.push('/matching'), 1500)
      } else if (result.matched) {
        const poolName = divingPools.find(p => p.id === formData.diving_pool)?.name || formData.diving_pool
        const poolRegion = divingPools.find(p => p.id === formData.diving_pool)?.region || '서울'

        const getTimeSlotLabel = (slot: string) => {
          switch(slot) {
            case 'morning': return '오전 (08:00-12:00)'
            case 'afternoon': return '오후 (12:00-18:00)'
            case 'evening': return '저녁 (18:00-20:00)'
            case 'flexible': return '시간 상관없음'
            default: return slot
          }
        }

        const members = result.matchInfo?.members || []
        const firstMember = members[0]
        const participantIds = members.map((m: any) => m.id)

        setMatchInfo({
          matchId: result.matchInfo?.matchId || result.requestId,
          partnerName: firstMember?.name || result.matchInfo?.partnerName || '매칭된 버디',
          partnerLevel: firstMember?.certification_level || result.matchInfo?.partnerLevel,
          date: formData.date,
          time: getTimeSlotLabel(formData.time_slot),
          location: result.matchInfo?.location || poolName,
          region: poolRegion,
          partnerUserId: firstMember?.id || result.matchInfo?.partnerUserId,
          participantIds: participantIds,
          participants: members.map((m: any) => ({
            id: m.id,
            name: m.name,
            level: m.certification_level
          }))
        })
        setAdditionalDivers(result.additionalDivers || [])
        setShowMatchingModal(true)
      } else {
        setIsWaitingForMatch(true)
        toast.success('매칭 요청이 등록되었습니다!', `${result.message || '조건에 맞는 버디가 나타나면 알려드릴게요.'} 실시간 알림이 활성화되었습니다.`)

        setTimeout(() => {
          if (isWaitingForMatch) {
            router.push('/matching')
          }
        }, 3000)
      }
    } catch (error: any) {
      logger.error('Error creating matching request:', error)
      logger.error('Error details:', {
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code
      })
      toast.error('매칭 요청 생성 실패', error?.message || '잠시 후 다시 시도해주세요.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleMatchModalClose = () => {
    setShowMatchingModal(false)
    router.push('/matching')
  }

  const handleMatchModalConfirm = async () => {
    try {
      if (matchInfo?.partnerUserId) {
        const authToken = localStorage.getItem('supabase_token')
        if (authToken) {
          await fetch('/api/chat/rooms', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`
            },
            body: JSON.stringify({
              partner_ids: [matchInfo.partnerUserId],
              match_id: matchInfo.matchId
            })
          })
        }
      }
    } catch (error) {
      logger.error('Failed to create chat room:', error)
    }

    setShowMatchingModal(false)
    if (matchInfo?.matchId) {
      router.push(`/matching/${matchInfo.matchId}`)
    } else {
      router.push('/matching')
    }
  }

  const handleInviteGroup = async (diverIds: string[]) => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('로그인이 필요합니다')
      }

      const response = await fetch('/api/matching/group-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          matchId: matchInfo?.matchId,
          invitedUserIds: diverIds,
          matchInfo
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '그룹 초대에 실패했습니다')
      }

      const result = await response.json()
    } catch (error: any) {
      logger.error('그룹 초대 실패:', error)
      toast.error('그룹 초대 실패', error?.message || '잠시 후 다시 시도해주세요.')
    }
  }

  if (isLoading) {
    return (
      <div className={`min-h-screen py-8 ${
        theme === 'light'
          ? 'bg-[#f4f7fb]'
          : 'bg-[#0f172a]'
      }`}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <ButtonSkeleton className="h-10 w-10" />
            <div className="h-8 w-48 rounded animate-pulse bg-gray-200 dark:bg-gray-800" />
          </div>
          <div className="rounded-2xl p-6 space-y-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg">
            <TextSkeleton lines={5} />
            <div className="grid grid-cols-2 gap-4">
              <ButtonSkeleton className="h-10" />
              <ButtonSkeleton className="h-10" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen py-3 xxs:py-4 sm:py-8 ${
      theme === 'light'
        ? 'bg-[#f4f7fb]'
        : 'bg-[#0f172a]'
    }`}>
      <div className="max-w-2xl mx-auto px-2 xxs:px-3 sm:px-4">
        {/* 헤더 */}
        <div className="flex items-center gap-2 xxs:gap-3 mb-4 xxs:mb-6 sm:mb-8">
          <button
            onClick={() => router.back()}
            className="p-1.5 xxs:p-2.5 sm:p-2 rounded-lg transition-colors touch-manipulation bg-white dark:bg-gray-800 text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
          >
            <ChevronLeft className="w-4 xxs:w-5 h-4 xxs:h-5" />
          </button>
          <h1 className="text-base xxs:text-lg xs:text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">새 매칭 요청</h1>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit}>
          <div className={`rounded-2xl p-3 xxs:p-4 sm:p-6 space-y-4 xxs:space-y-5 sm:space-y-6 ${
            theme === 'light'
              ? 'bg-white border border-gray-200 shadow-lg'
              : 'bg-gray-800'
          }`}>
            {/* 1단계: 다이빙풀 선택 */}
            <div>
              <label className={`block text-xs xxs:text-sm font-medium mb-1.5 xxs:mb-2 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                <Waves className="w-3 xxs:w-4 h-3 xxs:h-4 inline mr-1" />
                다이빙풀 선택
              </label>
              <select
                value={formData.diving_pool}
                onChange={(e) => {
                  const poolId = e.target.value
                  setFormData({ ...formData, diving_pool: poolId })
                }}
                className={`w-full px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 xs:py-3 border rounded-lg focus:ring-2 focus:ring-[#2b6df0] text-xs xxs:text-sm xs:text-base touch-manipulation ${
                  theme === 'light'
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-gray-700 border-gray-600 text-white'
                } ${theme}`}
                style={{
                  fontSize: '16px',
                  minHeight: '44px'
                }}
                required
              >
                <option value="">다이빙풀을 선택하세요</option>
                {divingPools.map(pool => (
                  <option key={pool.id} value={pool.id}>
                    {pool.name} ({pool.depth} / {pool.region})
                  </option>
                ))}
              </select>
            </div>
            
            {/* 2단계: 날짜 선택 */}
            <div>
              <label className={`block text-xs xxs:text-sm font-medium mb-1.5 xxs:mb-2 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                <Calendar className="w-3 xxs:w-4 h-3 xxs:h-4 inline mr-1" />
                날짜
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => {
                    const newDate = e.target.value
                    setFormData({ ...formData, date: newDate })
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className={`w-full px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 xs:py-3 border rounded-lg focus:ring-2 focus:ring-[#2b6df0] cursor-pointer text-xs xxs:text-sm xs:text-base ${
                    theme === 'light'
                      ? 'bg-white border-gray-300 text-black'
                      : 'bg-gray-700 border-gray-600 text-white'
                  } ${theme}`}
                  style={{
                    colorScheme: 'dark',
                    fontSize: '16px',
                    minHeight: '44px'
                  }}
                  onFocus={(e) => {
                    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                      (e.target as any).showPicker?.()
                    }
                  }}
                  onClick={(e) => {
                    (e.target as any).showPicker?.()
                  }}
                  required
                />
              </div>
            </div>
            
            {/* 3단계: 시간대 선택 */}
            <div>
              <label className={`block text-xs xxs:text-sm font-medium mb-1.5 xxs:mb-2 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                <Clock className="w-3 xxs:w-4 h-3 xxs:h-4 inline mr-1" />
                시간대
              </label>

              <div className="space-y-2 xxs:space-y-3">
                {/* 오전 (08:00-12:00) */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border transition-all touch-manipulation ${
                  !isTimeSlotAvailable('morning', formData.date)
                    ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                    : formData.time_slot === 'morning'
                      ? theme === 'light'
                        ? 'border-[#2b6df0] bg-[#e9eff7] cursor-pointer'
                        : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)] cursor-pointer'
                      : theme === 'light'
                        ? 'border-[#e9eff7] cursor-pointer'
                        : 'border-[#2c3a52] cursor-pointer'
                }`}>
                  <input
                    type="radio"
                    name="time_slot"
                    checked={formData.time_slot === 'morning'}
                    onChange={() => setFormData({ ...formData, time_slot: 'morning' })}
                    disabled={!isTimeSlotAvailable('morning', formData.date)}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0] disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      !isTimeSlotAvailable('morning', formData.date)
                        ? 'text-gray-400 dark:text-gray-500'
                        : formData.time_slot === 'morning'
                          ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                          : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      🌅 오전 (08:00-12:00) {!isTimeSlotAvailable('morning', formData.date) && '- 마감'}
                    </div>
                  </div>
                </label>

                {/* 오후 (12:00-18:00) */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border transition-all touch-manipulation ${
                  !isTimeSlotAvailable('afternoon', formData.date)
                    ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                    : formData.time_slot === 'afternoon'
                      ? theme === 'light'
                        ? 'border-[#2b6df0] bg-[#e9eff7] cursor-pointer'
                        : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)] cursor-pointer'
                      : theme === 'light'
                        ? 'border-[#e9eff7] cursor-pointer'
                        : 'border-[#2c3a52] cursor-pointer'
                }`}>
                  <input
                    type="radio"
                    name="time_slot"
                    checked={formData.time_slot === 'afternoon'}
                    onChange={() => setFormData({ ...formData, time_slot: 'afternoon' })}
                    disabled={!isTimeSlotAvailable('afternoon', formData.date)}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0] disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      !isTimeSlotAvailable('afternoon', formData.date)
                        ? 'text-gray-400 dark:text-gray-500'
                        : formData.time_slot === 'afternoon'
                          ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                          : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      ☀️ 오후 (12:00-18:00) {!isTimeSlotAvailable('afternoon', formData.date) && '- 마감'}
                    </div>
                  </div>
                </label>

                {/* 저녁 (18:00-20:00) */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border transition-all touch-manipulation ${
                  !isTimeSlotAvailable('evening', formData.date)
                    ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                    : formData.time_slot === 'evening'
                      ? theme === 'light'
                        ? 'border-[#2b6df0] bg-[#e9eff7] cursor-pointer'
                        : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)] cursor-pointer'
                      : theme === 'light'
                        ? 'border-[#e9eff7] cursor-pointer'
                        : 'border-[#2c3a52] cursor-pointer'
                }`}>
                  <input
                    type="radio"
                    name="time_slot"
                    checked={formData.time_slot === 'evening'}
                    onChange={() => setFormData({ ...formData, time_slot: 'evening' })}
                    disabled={!isTimeSlotAvailable('evening', formData.date)}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0] disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      !isTimeSlotAvailable('evening', formData.date)
                        ? 'text-gray-400 dark:text-gray-500'
                        : formData.time_slot === 'evening'
                          ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                          : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      🏙️ 저녁 (18:00-20:00) {!isTimeSlotAvailable('evening', formData.date) && '- 마감'}
                    </div>
                  </div>
                </label>

                {/* 시간 상관없음 */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border transition-all touch-manipulation ${
                  !isTimeSlotAvailable('flexible', formData.date)
                    ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                    : formData.time_slot === 'flexible'
                      ? theme === 'light'
                        ? 'border-[#2b6df0] bg-[#e9eff7] cursor-pointer'
                        : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)] cursor-pointer'
                      : theme === 'light'
                        ? 'border-[#e9eff7] cursor-pointer'
                        : 'border-[#2c3a52] cursor-pointer'
                }`}>
                  <input
                    type="radio"
                    name="time_slot"
                    checked={formData.time_slot === 'flexible'}
                    onChange={() => setFormData({ ...formData, time_slot: 'flexible' })}
                    disabled={!isTimeSlotAvailable('flexible', formData.date)}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0] disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      !isTimeSlotAvailable('flexible', formData.date)
                        ? 'text-gray-400 dark:text-gray-500'
                        : formData.time_slot === 'flexible'
                          ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                          : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      ⏰ 시간 상관없음 {!isTimeSlotAvailable('flexible', formData.date) && '- 오늘 마감'}
                    </div>
                    <div className={`text-[10px] xxs:text-xs mt-0.5 ${
                      theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      매칭 확률이 가장 높습니다
                    </div>
                  </div>
                </label>
              </div>

              <div className={`mt-1.5 xxs:mt-2 p-2 xxs:p-2.5 xs:p-3 rounded-lg border ${
                theme === 'light' ? 'bg-blue-50 border-blue-200' : 'bg-blue-500/10 border-blue-500/20'
              }`}>
                <p className={`text-[10px] xxs:text-xs flex items-center gap-1 break-keep ${
                  theme === 'light' ? 'text-blue-700' : 'text-blue-300'
                }`}>
                  <Clock className="w-2.5 xxs:w-3 h-2.5 xxs:h-3 flex-shrink-0" />
                  <strong>안내:</strong> 다이빙 풀장마다 운영시간이 다릅니다. 매칭 후 직접 풀장에 예약하셔야 합니다.
                </p>
              </div>
            </div>

            {/* 선호 버디 레벨 */}
            <div>
              <label className={`block text-xs xxs:text-sm font-medium mb-1.5 xxs:mb-2 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                <Users className="w-3 xxs:w-4 h-3 xxs:h-4 inline mr-1" />
                선호 버디 레벨
              </label>

              {/* 레벨 상관없음 체크박스 */}
              <div className="mb-2 xxs:mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.level_flexible}
                    onChange={(e) => {
                      const isFlexible = e.target.checked
                      setFormData({
                        ...formData,
                        level_flexible: isFlexible,
                        experience_level: isFlexible ? '' : formData.experience_level
                      })
                    }}
                    className={`mr-1.5 xxs:mr-2 rounded w-3.5 xxs:w-4 h-3.5 xxs:h-4 touch-manipulation ${
                      theme === 'light' ? 'bg-gray-200 border-gray-300' : 'bg-gray-700 border-gray-600'
                    } text-[#2b6df0] focus:ring-[#2b6df0]`}
                  />
                  <span className={`text-xs xxs:text-sm break-keep ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>레벨 상관 없음 (모든 레벨과 매칭)</span>
                </label>
              </div>

              <select
                value={formData.experience_level}
                onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
                disabled={formData.level_flexible}
                className={`w-full px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 xs:py-3 border rounded-lg focus:ring-2 focus:ring-[#2b6df0] text-xs xxs:text-sm xs:text-base touch-manipulation ${
                  formData.level_flexible
                    ? theme === 'light'
                      ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed'
                    : theme === 'light'
                      ? 'bg-white border-gray-300 text-black'
                      : 'bg-gray-700 border-gray-600 text-white'
                } ${theme}`}
                style={{
                  fontSize: '16px',
                  minHeight: '44px'
                }}
              >
                <option value="">레벨 상관없음</option>
                
                <optgroup label="📚 입문자">
                  <option value="beginner">입문자</option>
                </optgroup>
                
                <optgroup label="🤿 AIDA (프리다이빙)">
                  <option value="aida1">AIDA 1 (입문)</option>
                  <option value="aida2">AIDA 2 (기초)</option>
                  <option value="aida3">AIDA 3 (중급)</option>
                  <option value="aida4">AIDA 4 (상급)</option>
                </optgroup>
                
                <optgroup label="🌍 CMAS (프리다이빙)">
                  <option value="cmas1">CMAS 1 (입문)</option>
                  <option value="cmas2">CMAS 2 (기초)</option>
                  <option value="cmas3">CMAS 3 (중급)</option>
                  <option value="cmas4">CMAS 4 (상급)</option>
                </optgroup>
                
                <optgroup label="⭐ SSI (프리다이빙)">
                  <option value="ssi_basic">Basic Freediver</option>
                  <option value="ssi_level1">Level 1</option>
                  <option value="ssi_level2">Level 2</option>
                  <option value="ssi_level3">Level 3</option>
                </optgroup>
                
                <optgroup label="🌊 PADI (프리다이빙)">
                  <option value="padi_basic">Basic Freediver</option>
                  <option value="padi_freediver">Freediver</option>
                  <option value="padi_advanced">Advanced Freediver</option>
                  <option value="padi_master">Master Freediver</option>
                </optgroup>
                
                <optgroup label="🚀 RAID (프리다이빙)">
                  <option value="raid_basic">Basic Freediver</option>
                  <option value="raid_freediver">Freediver</option>
                  <option value="raid_advanced">Advanced Freediver</option>
                  <option value="raid_master">Master Freediver</option>
                </optgroup>
                
                <optgroup label="🏆 강사급">
                  <option value="instructor">강사</option>
                  <option value="instructor_trainer">강사 트레이너</option>
                </optgroup>
              </select>

              <p className={`mt-0.5 xxs:mt-1 text-[10px] xxs:text-xs break-keep ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                비슷한 레벨의 버디와 매칭됩니다
              </p>
            </div>

            {/* 그룹 크기 선택 */}
            <div>
              <label className={`block text-xs xxs:text-sm font-medium mb-1.5 xxs:mb-2 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                <Users className="w-3 xxs:w-4 h-3 xxs:h-4 inline mr-1" />
                그룹 크기 선택
              </label>
              <div className="space-y-2 xxs:space-y-3">
                {/* 1:1 매칭 (2명) */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border cursor-pointer transition-all touch-manipulation ${
                  formData.preferred_group_size === 2
                    ? theme === 'light'
                      ? 'border-[#2b6df0] bg-[#e9eff7]'
                      : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)]'
                    : theme === 'light'
                      ? 'border-[#e9eff7]'
                      : 'border-[#2c3a52]'
                }`}>
                  <input
                    type="radio"
                    name="group_size"
                    checked={formData.preferred_group_size === 2}
                    onChange={() => setFormData({ ...formData, preferred_group_size: 2 })}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0]"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      formData.preferred_group_size === 2
                        ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                        : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      👥 1:1 매칭 (2명)
                    </div>
                    <div className={`text-[10px] xxs:text-xs mt-0.5 ${
                      theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      나와 한 명의 버디만 매칭됩니다
                    </div>
                  </div>
                </label>

                {/* 그룹 매칭 (3명) */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border cursor-pointer transition-all touch-manipulation ${
                  formData.preferred_group_size === 3
                    ? theme === 'light'
                      ? 'border-[#2b6df0] bg-[#e9eff7]'
                      : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)]'
                    : theme === 'light'
                      ? 'border-[#e9eff7]'
                      : 'border-[#2c3a52]'
                }`}>
                  <input
                    type="radio"
                    name="group_size"
                    checked={formData.preferred_group_size === 3}
                    onChange={() => setFormData({ ...formData, preferred_group_size: 3 })}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0]"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      formData.preferred_group_size === 3
                        ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                        : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      👨‍👩‍👧 그룹 매칭 (3명)
                    </div>
                    <div className={`text-[10px] xxs:text-xs mt-0.5 ${
                      theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      씽 3명이 함께 다이빙합니다
                    </div>
                  </div>
                </label>

                {/* 그룹 매칭 (4명) */}
                <label className={`flex items-center p-2 xxs:p-3 rounded-lg border cursor-pointer transition-all touch-manipulation ${
                  formData.preferred_group_size === 4
                    ? theme === 'light'
                      ? 'border-[#2b6df0] bg-[#e9eff7]'
                      : 'border-[#5ea6ff] bg-[rgba(43,109,240,0.1)]'
                    : theme === 'light'
                      ? 'border-[#e9eff7]'
                      : 'border-[#2c3a52]'
                }`}>
                  <input
                    type="radio"
                    name="group_size"
                    checked={formData.preferred_group_size === 4}
                    onChange={() => setFormData({ ...formData, preferred_group_size: 4 })}
                    className="mr-2 xxs:mr-3 w-4 h-4 text-[#2b6df0] focus:ring-[#2b6df0]"
                  />
                  <div className="flex-1">
                    <div className={`font-medium text-xs xxs:text-sm ${
                      formData.preferred_group_size === 4
                        ? theme === 'light' ? 'text-[#2b6df0]' : 'text-[#5ea6ff]'
                        : theme === 'light' ? 'text-gray-800' : 'text-white'
                    }`}>
                      👨‍👩‍👧‍👦 그룹 매칭 (4명)
                    </div>
                    <div className={`text-[10px] xxs:text-xs mt-0.5 ${
                      theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      씽 4명이 함께 다이빙합니다
                    </div>
                  </div>
                </label>

              </div>
              <p className={`mt-1.5 xxs:mt-2 text-[10px] xxs:text-xs break-keep ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                💡 그룹 매칭을 선택하면 더 많은 다이버들과 함께할 수 있습니다
              </p>
            </div>

            {/* 추가 메시지 */}
            <div>
              <label className={`block text-xs xxs:text-sm font-medium mb-1.5 xxs:mb-2 ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'}`}>
                💬 추가 메시지 (선택)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="버디에게 전달하고 싶은 메시지가 있다면 적어주세요. (예: 프리다이빙 초보입니다, 스태틱만 연습하고 싶어요)"
                className={`w-full px-2 xxs:px-3 xs:px-4 py-2 xxs:py-2.5 xs:py-3 border rounded-lg focus:ring-2 focus:ring-[#2b6df0] resize-none text-xs xxs:text-sm xs:text-base touch-manipulation ${
                  theme === 'light'
                    ? 'bg-white border-gray-300 text-black'
                    : 'bg-gray-700 border-gray-600 text-white'
                } ${theme}`}
                style={{
                  fontSize: '16px',
                  minHeight: '70px'
                }}
                rows={3}
                maxLength={200}
              />
              <p className={`mt-0.5 xxs:mt-1 text-[10px] xxs:text-xs ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
                {formData.description.length}/200자
              </p>
            </div>


            {/* 버튼 */}
            <div className="flex gap-2 xxs:gap-2.5 xs:gap-3">
              <button
                type="button"
                onClick={() => router.back()}
                className={`flex-1 py-2.5 xxs:py-3 xs:py-4 px-2 xxs:px-3 xs:px-4 rounded-lg transition-colors touch-manipulation text-xs xxs:text-sm xs:text-base whitespace-nowrap ${
                  theme === 'light' ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' : 'bg-gray-700 text-white hover:bg-gray-600'
                }`}
                style={{ minHeight: '44px' }}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`flex-1 py-2.5 xxs:py-3 xs:py-4 px-2 xxs:px-3 xs:px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-1.5 xxs:gap-2 touch-manipulation text-xs xxs:text-sm xs:text-base whitespace-nowrap ${
                  isSaving
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-[#0a1f3a] text-white'
                }`}
                style={{ minHeight: '44px' }}
              >
                <Save className="w-4 xxs:w-4.5 xs:w-5 h-4 xxs:h-4.5 xs:h-5" />
                {isSaving ? '저장 중...' : '매칭 시작'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* 매칭 성공 모달 */}
      <MatchingSuccessModal
        matchInfo={matchInfo}
        additionalDivers={additionalDivers}
        isOpen={showMatchingModal}
        onClose={handleMatchModalClose}
        onConfirm={handleMatchModalConfirm}
        onInviteGroup={handleInviteGroup}
      />
    </div>
  )
}
